import { create } from 'zustand';
import type { Avatar, DropView, Equipped, Player } from '../models/types';
import { api } from '../services/api';
import { currentPosition } from '../services/geolocation';
import { Logger } from '../services/logger';
import { useAchievementAlerts } from './achievementAlerts';

const log = new Logger('Player');

interface PlayerState {
  /** `undefined` until the first load settles. */
  player: Player | null;
  loading: boolean;
  error: string | null;
  load: () => Promise<void>;
  reset: () => void;
  refresh: () => Promise<void>;
  /** Send the device position so hunt hints are measured from here. Rejects on failure. */
  refreshLocation: () => Promise<void>;
  applyRealtime: (player: Player) => void;
  updateDisplayName: (name: string) => Promise<void>;
  setEquipped: (equipped: Equipped) => Promise<void>;
  /** Drop an owned item where the player stands (fresh position read). Rejects on failure. */
  dropItem: (instanceId: string) => Promise<DropView>;
}

// Outfit saves are sent one at a time (port of the Flutter PlayerNotifier): while a PUT
// is in flight, later outfits wait as a single pending outfit (latest wins), so responses
// can never arrive out of order and an earlier failure never wipes a later tap.
let confirmed: Avatar | null = null;
/** `updatedAt` of the newest avatar accepted so far; older realtime avatars are stale. */
let latestAvatarAt: number | null = null;
let inFlight = false;
let pending: Equipped | null = null;
let waiters: { resolve: () => void; reject: (e: unknown) => void }[] = [];

const saving = () => inFlight || pending !== null;

function noteConfirmed(avatar: Avatar) {
  confirmed = avatar;
  if (avatar.updatedAt != null && (latestAvatarAt == null || avatar.updatedAt > latestAvatarAt)) latestAvatarAt = avatar.updatedAt;
}

/** Test seam: forget all save bookkeeping. */
export function resetPlayerBookkeeping() {
  confirmed = null;
  latestAvatarAt = null;
  inFlight = false;
  pending = null;
  waiters = [];
}

export const usePlayer = create<PlayerState>((set, get) => {
  /**
   * Take a server payload. While a save is in flight or pending its avatar may predate the
   * newest tap, so only the rest is applied; once settled, an avatar older than the newest
   * one already accepted is ignored too.
   */
  function accept(player: Player) {
    const current = get().player;
    const incomingAt = player.avatar.updatedAt;
    useAchievementAlerts.getState().observe(player.achievements);
    const stale = latestAvatarAt != null && (incomingAt == null || incomingAt < latestAvatarAt);
    if (current && (saving() || stale)) {
      set({ player: { ...player, avatar: current.avatar }, error: null });
      return;
    }
    noteConfirmed(player.avatar);
    set({ player, error: null });
  }

  async function send(outfit: Equipped): Promise<void> {
    inFlight = true;
    let error: unknown;
    let failed = false;
    let saved: Avatar | undefined;
    try {
      saved = await api.putAvatar(outfit);
      noteConfirmed(saved);
    } catch (e) {
      failed = true;
      error = e;
      log.warn('avatar save failed', { error: e });
    }
    inFlight = false;

    const next = pending;
    if (next) {
      // The screen already shows the newest outfit; keep it and send that next.
      pending = null;
      void send(next);
      return;
    }

    const w = waiters;
    waiters = [];
    const current = get().player;
    if (!failed) {
      if (current && saved) set({ player: { ...current, avatar: saved } });
      for (const x of w) x.resolve();
    } else {
      if (current && confirmed) set({ player: { ...current, avatar: confirmed } });
      for (const x of w) x.reject(error);
    }
  }

  return {
    player: null,
    loading: false,
    error: null,
    load: async () => {
      set({ loading: true, error: null });
      try {
        const player = await api.getPlayer();
        noteConfirmed(player.avatar);
        useAchievementAlerts.getState().observe(player.achievements);
        set({ player, loading: false });
      } catch (e) {
        log.error('load failed', { error: e });
        set({ loading: false, error: e instanceof Error ? e.message : String(e) });
      }
    },
    reset: () => {
      resetPlayerBookkeeping();
      useAchievementAlerts.getState().observe(null);
      set({ player: null, loading: false, error: null });
    },
    refresh: async () => {
      try {
        accept(await api.getPlayer());
      } catch (e) {
        log.error('refresh failed', { error: e });
        if (!get().player) set({ error: e instanceof Error ? e.message : String(e) });
      }
    },
    refreshLocation: async () => {
      const pos = await currentPosition();
      accept(await api.refreshLocation({ lat: pos.latitude, lng: pos.longitude, accuracy: pos.accuracy }));
    },
    applyRealtime: (player) => accept(player),
    dropItem: async (instanceId) => {
      const pos = await currentPosition();
      const drop = await api.dropItem(instanceId, { lat: pos.latitude, lng: pos.longitude, accuracy: pos.accuracy });
      // Leave the collection right away; the refresh (and realtime) bring the rest, such as
      // the item being taken off the avatar.
      const current = get().player;
      if (current) set({ player: { ...current, inventory: current.inventory.filter((i) => i.instanceId !== instanceId) } });
      void get().refresh();
      return drop;
    },
    updateDisplayName: async (name) => {
      accept(await api.updateDisplayName(name));
    },
    /**
     * Show `equipped` immediately and save it as the complete new outfit. Resolves when the
     * save carrying this outfit (or a later one) settles; rejects if that last save failed,
     * after the avatar has been restored to the confirmed one.
     */
    setEquipped: (equipped) => {
      const current = get().player;
      if (!current) return Promise.reject(new Error('No player loaded'));
      confirmed ??= current.avatar;
      set({ player: { ...current, avatar: { ...current.avatar, equipped: { ...equipped } } } });
      return new Promise<void>((resolve, reject) => {
        waiters.push({ resolve, reject });
        if (inFlight) pending = { ...equipped };
        else void send({ ...equipped });
      });
    },
  };
});
